import { calculator } from "@dinero.js/calculator-number"
import { ComparisonOperator, down } from "@dinero.js/core"
import { DKK } from "@dinero.js/currencies"
import {
    type DivideOperation,
    type Transformer,
    dinero,
    multiply as multiplyDn,
    toDecimal as toDecimalDn,
} from "dinero.js"
import { ScaledAmount } from "./amountTypes"

export const SCALED_AMOUNT_ZERO: ScaledAmount = { amount: 0, scale: 0 }
export const SCALED_AMOUNT_ONE: ScaledAmount = { amount: 1, scale: 0 }

const equalFn = (x: number, y: number) => calculator.compare(x, y) === ComparisonOperator.EQ

const lessThanFn = (x: number, y: number) => calculator.compare(x, y) === ComparisonOperator.LT

const greaterThanFn = (x: number, y: number) => calculator.compare(x, y) === ComparisonOperator.GT

const maximumFn = (values: readonly number[]) => {
    return values.reduce((acc, curr) => {
        return lessThanFn(acc, curr) ? curr : acc
    })
}

export function movePointLeft(value: ScaledAmount, n: number): ScaledAmount {
    return {
        amount: value.amount,
        scale: value.scale + n,
    }
}

export function movePointRight(value: ScaledAmount, n: number): ScaledAmount {
    return {
        amount: value.amount,
        scale: value.scale - n,
    }
}

function countTrailingZeros(input: number, base: number) {
    const zero = calculator.zero()

    if (equalFn(zero, input)) {
        return zero
    }

    let i = zero
    let temp = input

    while (equalFn(calculator.modulo(temp, base), zero)) {
        temp = calculator.integerDivide(temp, base)
        i = calculator.increment(i)
    }

    return i
}

export function transformScale(value: ScaledAmount, newScale: number, divide: DivideOperation = down): ScaledAmount {
    const { amount, scale } = value

    const isLarger = greaterThanFn(newScale, scale)
    const operation = isLarger ? calculator.multiply : divide
    const [a, b] = isLarger ? [newScale, scale] : [scale, newScale]
    const base = 10

    const factor = calculator.power(base, calculator.subtract(a, b))

    return {
        amount: operation(amount, factor, calculator),
        scale: newScale,
    }
}

export function trimScale(value: ScaledAmount): ScaledAmount {
    const { amount, scale } = value
    const base = 10
    const minScale = 0

    const trailingZerosLength = countTrailingZeros(amount, base)
    const difference = calculator.subtract(scale, trailingZerosLength)
    const newScale = maximumFn([difference, minScale])

    if (equalFn(newScale, scale)) {
        return value
    }

    return transformScale(value, newScale)
}

export function normalizeScale(values: readonly ScaledAmount[]): ScaledAmount[] {
    const highestScale = values.reduce((highest, current) => {
        const { scale } = current
        return maximumFn([highest, scale])
    }, calculator.zero())

    return values.map((v) => {
        const { scale } = v

        if (equalFn(scale, highestScale)) {
            return v
        }

        return transformScale(v, highestScale)
    })
}

export function compare(x: ScaledAmount, y: ScaledAmount): ComparisonOperator {
    // Normalize scales to make comparison trivial
    const comparands = normalizeScale([x, y])
    const [{ amount: xAmount }, { amount: yAmount }] = comparands

    return calculator.compare(xAmount, yAmount)
}

export const equal = (x: ScaledAmount, y: ScaledAmount) => compare(x, y) === ComparisonOperator.EQ
export const greaterThan = (x: ScaledAmount, y: ScaledAmount) => compare(x, y) === ComparisonOperator.GT
export const lessThan = (x: ScaledAmount, y: ScaledAmount) => compare(x, y) === ComparisonOperator.LT

export function greaterThanOrEqual(x: ScaledAmount, y: ScaledAmount) {
    const cmp = compare(x, y)
    return cmp === ComparisonOperator.GT || cmp === ComparisonOperator.EQ
}

export function lessThanOrEqual(x: ScaledAmount, y: ScaledAmount) {
    const cmp = compare(x, y)
    return cmp === ComparisonOperator.LT || cmp === ComparisonOperator.EQ
}

export function add(values: ScaledAmount[]): ScaledAmount {
    // First normalize scales so all values have same scale
    const normalizedValues = normalizeScale(values)
    // Adding of scaled values with the same scale is trivial
    return normalizedValues.reduce((x, y) => ({ amount: x.amount + y.amount, scale: x.scale }))
}

export function subtract(x: ScaledAmount, y: ScaledAmount): ScaledAmount {
    // Normalize scales to make subtraction trivial
    const [a, b] = normalizeScale([x, y])
    return { amount: a.amount - b.amount, scale: a.scale }
}

export function multiply(x: ScaledAmount, y: ScaledAmount) {
    // TODO: Implement this properly instead of piggybacking on Dinero
    const dn = dinero({ amount: x.amount, scale: x.scale, currency: DKK })
    const dn2 = multiplyDn(dn, y)
    const { currency: _currency, ...result } = dn2.toJSON()
    return result
}

export function toDecimal(amount: ScaledAmount, transformer: (value: number) => string) {
    // TODO: Implement this properly instead of piggybacking on Dinero
    const dn = dinero({ amount: amount.amount, scale: amount.scale, currency: DKK })
    const tn: Transformer<number, string, string> = ({ value, currency: _currency }) => transformer(Number(value))
    return toDecimalDn(dn, tn)
}
